import * as React from 'react';
import PropTypes from 'prop-types';
import { alpha } from '@mui/material/styles';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Checkbox,
    IconButton,
    Tooltip,
    TablePagination,
    TextField,
    FormControlLabel,
    Switch,
    Typography,
    CircularProgress,
    Stack,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Chip,
    Divider,
    
} from "@mui/material";

import ArrowBack from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import FilterListIcon from '@mui/icons-material/FilterList';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import RefreshIcon from '@mui/icons-material/Refresh';
import { visuallyHidden } from '@mui/utils';
import { useState, useEffect, useMemo } from "react";
import ProcessActionsMenu from './ProcessActionsMenu';
import ProcessVersionsDialog from '../RobotsPageComponents/ProcessVersionsDialog';
import { useNavigate } from 'react-router-dom';
import { useToken } from '../items/UseToken';
import { ProcessesReadyState, SkillsReadyState } from '../ReadyStates';
import ReadOnlyWorkflowViewer from './ProcessDetailsFlowDialog';
import { TileButton } from '../Common/TileButton';

function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) return -1;
    if (b[orderBy] > a[orderBy]) return 1;
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

const headCells = [
    { id: 'name', numeric: false, disablePadding: false, label: 'Name' },
    { id: 'guid', numeric: false, disablePadding: false, label: 'ID' },
    { id: 'version', numeric: false, disablePadding: false, label: 'Active version' },
    { id: 'version_date', numeric: false, disablePadding: false, label: 'Version date' },
    { id: 'actions', numeric: false, disablePadding: false, label: 'Actions' },
];

function EnhancedTableHead(props) {
    const { onSelectAllClick, order, orderBy, numSelected, rowCount, onRequestSort } = props;
    const createSortHandler = (property) => (event) => onRequestSort(event, property);

    return (
        <TableRow>
            <TableCell padding="checkbox" sx={{ bgcolor: "background.paper" }}>
                <Checkbox
                    color="primary"
                    indeterminate={numSelected > 0 && numSelected < rowCount}
                    checked={rowCount > 0 && numSelected === rowCount}
                    onChange={onSelectAllClick}
                    inputProps={{ 'aria-label': 'select all rows' }}
                />
            </TableCell>

            {headCells.map((headCell) => (
                <TableCell
                    key={headCell.id}
                    align={headCell.numeric ? 'right' : 'left'}
                    padding={headCell.disablePadding ? 'none' : 'normal'}
                    sortDirection={orderBy === headCell.id ? order : false}
                    sx={{
                        bgcolor: "background.paper",
                        fontWeight: 900,
                        letterSpacing: 0.3,
                        whiteSpace: "nowrap",
                    }}
                >
                    {headCell.id === "actions" ? (
                        headCell.label
                    ) : (
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={createSortHandler(headCell.id)}
                        >
                            {headCell.label}
                            {orderBy === headCell.id ? (
                                <Box component="span" sx={visuallyHidden}>
                                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                </Box>
                            ) : null}
                        </TableSortLabel>
                    )}
                </TableCell>
            ))}
        </TableRow>
    );
}

EnhancedTableHead.propTypes = {
    numSelected: PropTypes.number.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    onSelectAllClick: PropTypes.func.isRequired,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
};

export default function ProcessTable({ skillCategory, emptyState, refreshTrigger }) {
    const [order, setOrder] = useState('desc');
    const [orderBy, setOrderBy] = useState('start_date');
    const [selected, setSelected] = useState([]);
    const [page, setPage] = useState(0);
    const [dense, setDense] = useState(false);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [rows, setRows] = useState([]);
    const [showFilter, setShowFilter] = useState(false);

    const [nameFilter, setNameFilter] = useState('');
    const [idFilter, setIdFilter] = useState('');

    const [versioningOpened, setVersioningOpened] = useState(false);
    const [versionRow, setVersionRow] = useState(null);

    const [forceRenderCounter, setForceRenderCounter] = useState(0);
    const [isLoaded, setIsLoaded] = useState(false);

    // ✅ delete modal
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState("");
    const [viewerOpen, setViewerOpen] = useState(false);
    const [showDrafts, setShowDrafts] = useState(false);


    const { token } = useToken();
    const navigate = useNavigate();

    const deleteEndpoint = "api/deleteprocess";


    const fetchData = () => {

        setIsLoaded(false);
        setRows([]);

        const effectiveCategory = showDrafts ? "draft" : skillCategory;
        const query = `api/getrobots?is_active=1&category=${effectiveCategory}`;

        fetch(query, {
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + token,
            }
        })
            .then(res => res.json())
            .then(res => {
                const base = res.results || [];
                if (base.length < 1) {
                    setIsLoaded(true);
                    return;
                }

                Promise.all(
                    base.map((row) =>
                        fetch("api/getrobots?maxversion=true&guid=" + row.guid, {
                            headers: {
                                "Content-Type": "application/json",
                                "Authorization": "Bearer " + token,
                            }
                        })
                            .then(r => r.json())
                            .then(r => ({ ...row, max: r?.results?.[0]?.version ?? row.version }))
                            .catch(() => ({ ...row, max: row.version }))
                    )
                ).then((rowsWithMax) => {
                    setRows(rowsWithMax);
                    setIsLoaded(true);
                });
            })
            .catch(err => {
                console.log(err);
                setIsLoaded(true);
            });
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [forceRenderCounter, refreshTrigger, showDrafts]);

    useEffect(() => {
        skillCategory === "skill" ? navigate('/resources/skills') : navigate('/resources/processes');

    }, [])

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const newSelecteds = visibleRows.map((n) => n.guid);
            setSelected(newSelecteds);
            return;
        }
        setSelected([]);
    };

    const handleClick = (event, guid) => {
        const selectedIndex = selected.indexOf(guid);
        let newSelected = [];

        if (selectedIndex === -1) newSelected = newSelected.concat(selected, guid);
        else if (selectedIndex === 0) newSelected = newSelected.concat(selected.slice(1));
        else if (selectedIndex === selected.length - 1) newSelected = newSelected.concat(selected.slice(0, -1));
        else if (selectedIndex > 0) newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));

        setSelected(newSelected);
    };

    const handleChangePage = (event, newPage) => setPage(newPage);

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleChangeDense = (event) => setDense(event.target.checked);

    const isSelected = (guid) => selected.indexOf(guid) !== -1;
    const showEmpty = rows.length === 0;

    const emptyRows =
        page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

    const handleShowFilterChange = () => setShowFilter((p) => !p);
    const handleNameFilterChange = (event) => setNameFilter(event.target.value);
    const handleIdFilterChange = (event) => setIdFilter(event.target.value);

    const clearFilters = () => {
        setNameFilter("");
        setIdFilter("");
        setPage(0);
    };

    const hasAnyFilter = useMemo(() => {
        return (nameFilter ?? "").length > 0 || (idFilter ?? "").length > 0;
    }, [nameFilter, idFilter]);

    const openVersions = (row) => {
        setVersionRow(row);
        setVersioningOpened(true);
    };

    const closeVersions = () => {
        setVersioningOpened(false);
        setVersionRow(null);
    };

    const openDeleteConfirm = () => {
        setDeleteError("");
        setConfirmOpen(true);
    };
    const closeDeleteConfirm = () => {
        if (deleting) return;
        setConfirmOpen(false);
    };

    const confirmDelete = async () => {
        if (selected.length === 0) {
            setConfirmOpen(false);
            return;
        }

        setDeleting(true);
        setDeleteError("");

        try {
            await Promise.all(
                selected.map((guid) =>
                    fetch(deleteEndpoint, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            "Authorization": "Bearer " + token,
                        },
                        body: JSON.stringify({ guid }),
                    }).then(async (r) => {
                        if (!r.ok) {
                            const txt = await r.text().catch(() => "");
                            throw new Error(txt || `Delete failed for ${guid}`);
                        }
                    })
                )
            );

            setSelected([]);
            setConfirmOpen(false);
            setForceRenderCounter((x) => x + 1);
        } catch (e) {
            setDeleteError(e?.message || "Failed to delete one or more items.");
        } finally {
            setDeleting(false);
        }
    };

    function DraftButton(props)  {

        return (
            <Tooltip title={showDrafts ? "Hide drafts" : "Show drafts"}>
                <Box
                    onClick={() => {
                        setShowDrafts((v) => !v);
                        setPage(0);
                        setSelected([]);
                    }}
                    sx={{
                        ml: 1,
                        height: 30,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.8,
                        px: 1.2,
                        borderRadius: 999,
                        cursor: "pointer",
                        userSelect: "none",
                        border: "1px solid",
                        borderColor: showDrafts ? "primary.main" : "divider",
                        bgcolor: showDrafts ? alpha("#774CBC", 0.12) : alpha("#111827", 0.03),
                        boxShadow: showDrafts ? "0 10px 24px rgba(17,24,39,0.10)" : "none",
                        transition: "all 160ms ease",
                        "&:hover": {
                            bgcolor: showDrafts ? alpha("#774CBC", 0.16) : alpha("#111827", 0.06),
                            transform: "translateY(-0.5px)",
                        },
                        "&:active": {
                            transform: "translateY(0px)",
                        },
                    }}
                >
                    {/* mała ikonka w kółku */}
                    <Box
                        sx={{
                            width: 22,
                            height: 22,
                            borderRadius: 999,
                            display: "grid",
                            placeItems: "center",
                            border: "1px solid",
                            borderColor: showDrafts ? alpha("#774CBC", 0.35) : "divider",
                            bgcolor: showDrafts ? alpha("#774CBC", 0.18) : "background.paper",
                            color: showDrafts ? "#774CBC" : "text.secondary",
                            transition: "all 160ms ease",
                        }}
                    >
                        <FilterListIcon sx={{ fontSize: 16 }} />
                    </Box>

                    {/* tekst */}
                    <Typography
                        sx={{
                            fontSize: 12,
                            fontWeight: 950,
                            letterSpacing: 0.25,
                            color: showDrafts ? "primary.main" : "text.secondary",
                            lineHeight: 1,
                            pr: 0.2,
                        }}
                    >
                        {showDrafts ? "Displaying drafts only" : "Show drafts"}
                    </Typography>

                    {/* mały status dot */}
                    <Box
                        sx={{
                            width: 8,
                            height: 8,
                            borderRadius: 999,
                            bgcolor: showDrafts ? "#22c55e" : alpha("#111827", 0.18),
                            transition: "all 160ms ease",
                        }}
                    />
                </Box>
            </Tooltip>
        )
    }
    const visibleRows = useMemo(() => {
        return rows.filter((r) => {
            const nameOk = !nameFilter || (r.name || "").toLowerCase().includes(nameFilter.toLowerCase());
            const idOk = !idFilter || (r.guid || "").toLowerCase().includes(idFilter.toLowerCase());
            return nameOk && idOk;
        });
    }, [rows, nameFilter, idFilter]);

    // unikamy sytuacji: selected > visible po filtrach
    useEffect(() => {
        const visibleIds = new Set(visibleRows.map((r) => r.guid));
        setSelected((prev) => prev.filter((id) => visibleIds.has(id)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nameFilter, idFilter, rows.length]);

    return (
        <>
            {!isLoaded ? (
                <Box sx={{ p: 10, alignItems: "center" }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <CircularProgress size={30} thickness={5} />
                        <Typography variant="header" color="text.secondary">
                            Loading {skillCategory} data
                        </Typography>
                    </Stack>
                </Box>
            ) : showEmpty ? (
                <><Box sx={{ p: 4 }}>
                        {emptyState}
                       
                    </Box >
                        {showDrafts &&
                            <Box sx={{ p: 4 }}>
                                <TileButton title="There are no drafts yet" description="Click to go back" onClick={() => setShowDrafts(false)} icon={<ArrowBack />}></TileButton>

                </Box >
            
        }
                        
                        </>
            ) : (
                <>
                    {skillCategory === "skill" ? <SkillsReadyState /> : <ProcessesReadyState />}
                            {viewerOpen && (
                                <ReadOnlyWorkflowViewer flowJsonString={versionRow.data} onClose={() => setViewerOpen(false)} />

                            )}
                    <Box sx={{ width: '100%' }}>
                        <Paper
                            elevation={0}
                            variant="outlined"
                            sx={{
                                width: "100%",
                                mb: 2,
                                borderRadius: 3,
                                overflow: "hidden",
                            }}
                        >
                            {/* Top bar (jak w GenericDataTable) */}
                            <Box
                                sx={{
                                    px: 2,
                                    py: 1.25,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    bgcolor: "background.paper",
                                }}
                            >
                                        <Typography sx={{ fontWeight: 900 }} variant="subtitle1">
                                            {showDrafts
                                                ? "Drafts"
                                                : (skillCategory === "skill" ? "Skills" : "Processes")}
                                        </Typography>


                                <Box sx={{ flex: 1 }} />
                                       
                                {selected.length > 0 && (
                                    <Chip size="small" label={`${selected.length} selected`} sx={{ mr: 0.5 }} />
                                )}
                                       

                                        <DraftButton />
                                <Tooltip title="Refresh">
                                    <IconButton
                                        onClick={fetchData}
                                        sx={{
                                            border: "1px solid",
                                            borderColor: "divider",
                                            borderRadius: 2,
                                        }}
                                        size="small"
                                    >
                                        <RefreshIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title={showFilter ? "Hide filters" : "Show filters"}>
                                    <IconButton
                                        onClick={handleShowFilterChange}
                                        sx={{
                                            border: "1px solid",
                                            borderColor: showFilter ? "primary.main" : "divider",
                                            borderRadius: 2,
                                        }}
                                        size="small"
                                    >
                                        <FilterListIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>

                                {selected.length > 0 && (
                                    <Tooltip title={`Delete (${selected.length})`}>
                                        <IconButton
                                            onClick={openDeleteConfirm}
                                            sx={{
                                                border: "1px solid",
                                                borderColor: "error.main",
                                                borderRadius: 2,
                                                color: "error.main",
                                            }}
                                            size="small"
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>

                            <Divider />

                            {/* Filters */}
                            {showFilter && (
                                <Box sx={{ p: 2, bgcolor: "background.paper" }}>
                                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                                            Filters
                                        </Typography>

                                        <Box sx={{ flex: 1, width: "100%" }}>
                                            <TableContainer>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell sx={{ py: 1 }}>
                                                                <TextField
                                                                    value={nameFilter}
                                                                    onChange={handleNameFilterChange}
                                                                    id="nameFilter"
                                                                    label="Name"
                                                                    variant="standard"
                                                                />
                                                            </TableCell>

                                                            <TableCell sx={{ py: 1 }}>
                                                                <TextField
                                                                    value={idFilter}
                                                                    onChange={handleIdFilterChange}
                                                                    id="idFilter"
                                                                    label="ID"
                                                                    variant="standard"
                                                                />
                                                            </TableCell>

                                                            <TableCell sx={{ py: 1 }}>
                                                                <TextField disabled label="Version" variant="standard" />
                                                            </TableCell>

                                                            <TableCell sx={{ py: 1 }} />
                                                            <TableCell align="right" sx={{ whiteSpace: "nowrap", py: 1 }}>
                                                                <Button
                                                                    size="small"
                                                                    onClick={clearFilters}
                                                                    disabled={!hasAnyFilter}
                                                                    sx={{ textTransform: "none", fontWeight: 700 }}
                                                                >
                                                                    Clear
                                                                </Button>
                                                            </TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                </Table>
                                            </TableContainer>
                                        </Box>
                                    </Stack>
                                </Box>
                            )}
                                   
                            {/* Table */}
                            <TableContainer sx={{ maxHeight: dense ? 520 : 640 }}>
                                <Table
                                    stickyHeader
                                    sx={{ minWidth: 750 }}
                                    aria-labelledby="tableTitle"
                                    size={dense ? 'small' : 'medium'}
                                >
                                    <TableHead>
                                        <EnhancedTableHead
                                            numSelected={selected.length}
                                            order={order}
                                            orderBy={orderBy}
                                            onSelectAllClick={handleSelectAllClick}
                                            onRequestSort={handleRequestSort}
                                            rowCount={visibleRows.length}
                                        />
                                    </TableHead>

                                    <TableBody>
                                        {stableSort(visibleRows, getComparator(order, orderBy))
                                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                            .map((row, index) => {
                                                const isItemSelected = isSelected(row.guid);
                                                const labelId = `enhanced-table-checkbox-${index}`;

                                                return (
                                                    <TableRow
                                                        hover
                                                        role="checkbox"
                                                        aria-checked={isItemSelected}
                                                        tabIndex={-1}
                                                        key={row.guid}
                                                        selected={isItemSelected}
                                                        sx={{
                                                            "&:hover td": { bgcolor: "action.hover" },
                                                        }}
                                                    >
                                                        <TableCell padding="checkbox">
                                                            <Checkbox
                                                                onClick={(event) => handleClick(event, row.guid)}
                                                                color="primary"
                                                                checked={isItemSelected}
                                                                inputProps={{ 'aria-labelledby': labelId }}
                                                            />
                                                        </TableCell>

                                                        <TableCell component="th" id={labelId} scope="row" padding="none" >
                                                            <IconButton
                                                                onClick={() => { setVersionRow(row); setViewerOpen(true); }}
                                                                color="primary"
                                                                aria-label="update version"
                                                                size="small"
                                                                sx={{
                                                                    ml: 0.5,
                                                                    borderRadius: 2,
                                                                    border: "1px solid",
                                                                    borderColor: "divider",
                                                                }}
                                                            >
                                                                {row.name}
                                                            </IconButton>
                                                           
                                                        </TableCell>

                                                        <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                                                            {row.guid}
                                                        </TableCell>

                                                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                            {row.version}
                                                            {row.max > row.version && (
                                                                <Tooltip title="Update available">
                                                                    <IconButton
                                                                        onClick={() => openVersions(row)}
                                                                        color="success"
                                                                        aria-label="update version"
                                                                        size="small"
                                                                        sx={{
                                                                            ml: 0.5,
                                                                            borderRadius: 2,
                                                                            border: "1px solid",
                                                                            borderColor: "divider",
                                                                        }}
                                                                    >
                                                                        <UpgradeIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}
                                                        </TableCell>

                                                        <TableCell>{row.version_date}</TableCell>

                                                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                                                            <ProcessActionsMenu
                                                                forceRenderCounter={forceRenderCounter}
                                                                setForceRenderCounter={setForceRenderCounter}
                                                                row={row}
                                                                maxversion={row.max}
                                                            />
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}

                                        {emptyRows > 0 && (
                                            <TableRow style={{ height: (dense ? 33 : 53) * emptyRows }}>
                                                <TableCell colSpan={6} />
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <TablePagination
                                rowsPerPageOptions={[5, 10, 25, 50]}
                                component="div"
                                count={visibleRows.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        </Paper>

                        <FormControlLabel
                            control={<Switch checked={dense} onChange={handleChangeDense} />}
                            label="Dense padding"
                                />
                               
                    </Box>

                    {/* ✅ Versions dialog */}
                    <Dialog open={versioningOpened} onClose={closeVersions} maxWidth="md" fullWidth>
                        <DialogTitle sx={{ fontWeight: 900 }}>
                            Versions of {versionRow?.name ?? ""}
                        </DialogTitle>
                        <DialogContent dividers>
                            {versionRow && (
                                <ProcessVersionsDialog row={versionRow} maxversion={versionRow.max} />
                            )}
                        </DialogContent>
                        <DialogActions sx={{ p: 2 }}>
                            <Button onClick={closeVersions} sx={{ textTransform: "none" }}>
                                Close
                            </Button>
                        </DialogActions>
                    </Dialog>

                    {/* ✅ Bulk delete confirmation */}
                    <Dialog open={confirmOpen} onClose={closeDeleteConfirm} maxWidth="sm" fullWidth>
                        <DialogTitle sx={{ fontWeight: 900 }}>
                            Delete {selected.length} item{selected.length === 1 ? "" : "s"}?
                        </DialogTitle>

                        <DialogContent dividers>
                            <Typography variant="body2" color="text.secondary">
                                This will delete the selected {skillCategory === "skill" ? "skills" : "processes"}. This action cannot be undone.
                            </Typography>

                            <Box
                                sx={{
                                    mt: 2,
                                    maxHeight: 180,
                                    overflow: "auto",
                                    fontFamily: "monospace",
                                    fontSize: 12,
                                    bgcolor: "action.hover",
                                    borderRadius: 2,
                                    p: 1.5,
                                    border: "1px solid",
                                    borderColor: "divider",
                                }}
                            >
                                {selected.map((id) => (
                                    <Box key={id} sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                        {id}
                                    </Box>
                                ))}
                                    </Box>
                                   
                            {deleteError && (
                                <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                                    {deleteError}
                                </Typography>
                            )}
                        </DialogContent>

                        <DialogActions sx={{ p: 2 }}>
                            <Button onClick={closeDeleteConfirm} disabled={deleting} sx={{ textTransform: "none" }}>
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                color="error"
                                onClick={confirmDelete}
                                disabled={deleting || selected.length === 0}
                                sx={{ textTransform: "none", fontWeight: 900 }}
                            >
                                {deleting ? "Deleting..." : "Delete"}
                            </Button>
                        </DialogActions>
                            </Dialog>

                </>
            )}
        </>
    );
}

ProcessTable.propTypes = {
    skillCategory: PropTypes.string.isRequired,
    emptyState: PropTypes.node,
    refreshTrigger: PropTypes.any,
};
